import { useMemo, useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { Flex, toast } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { ComplianceCaseTypeEnum, ScreeningRecordStatus } from "~/graphql/supergraphEnums";
import { useGetPicrPositionDetailsQuery } from "~/graphql/supergraphHooks";
import {
  type ComplianceCaseAction,
  type ComplianceCaseStatus,
  type ComplianceCaseSubStatus,
  type GetComplianceCaseQuery,
} from "~/graphql/supergraphTypes";
import {
  useBlockComplianceCaseReopening,
  useComplianceEntityOnboardingEnabled,
  usePicrRevampFeatureFlag,
} from "~/pages/Compliance/flags";
import { ComplianceRulesContext } from "../../complianceRulesContext";
import { escalationTakeoverPaths } from "../Escalations/EscalationTakeover/paths";
import ProposedEscalationsModal from "../Escalations/ProposedEscalationsModal";
import UnassignedProfileWarningModal from "../PaymentInstructionChange/UnassignedProfileWarningModal";
import AssignControls from "./AssignControls";
import CheckerControlsSelectMenu from "./CheckerControlsSelectMenu";
import ClosedControls from "./ClosedControls";
import { EscalationsMenu } from "./EscalationsMenu";
import {
  getEscalationsMenuFlags,
  shouldRenderEscalationsMenu,
} from "./EscalationsMenu/escalationsMenuUtils";
import MakerControlsSelectMenu from "./MakerControlsSelectMenu";
import { RelatedCasesPill } from "./RelatedCasesPill";
import { caseDetailsFooterWrapper, rightAlignedControlWrapper } from "./styles_vanilla.css";
import UnassignControls from "./UnassignControls";

type Props = {
  assignee?: string | undefined;
  assigneeRole: "maker" | "checker" | undefined;
  caseId: number;
  caseIsClosed: boolean;
  caseStatus: ComplianceCaseStatus | undefined | null;
  caseSubstatus: ComplianceCaseSubStatus | undefined | null;
  caseType: ComplianceCaseTypeEnum;
  programId: number;
  escalation?: NonNullable<
    NonNullable<GetComplianceCaseQuery["complianceCase"]>["escalations"]
  >[0];
  escalationsCount?: number;
  escalations?: NonNullable<GetComplianceCaseQuery["complianceCase"]>["escalations"];
  profilesForCase?: GetComplianceCaseQuery["complianceCase"] | null | undefined;
  caseResolution?: string | null | undefined;
};

const CaseDetailsFooter = ({
  assignee,
  assigneeRole,
  caseId,
  caseIsClosed,
  caseStatus,
  caseSubstatus,
  caseType,
  programId,
  escalation,
  escalationsCount,
  escalations,
  profilesForCase,
  caseResolution,
}: Props) => {
  const rules = useContext(ComplianceRulesContext);
  const actions = rules.caseActionOptions;
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingSubmitAction, setPendingSubmitAction] = useState<(() => void) | null>(null);
  const picrRevampEnabled = usePicrRevampFeatureFlag();
  const isCaseReopeningBlocked = useBlockComplianceCaseReopening();
  const internalEscalationsEnabled = useComplianceEntityOnboardingEnabled();
  const history = useHistory();
  const [showReadOnlyEscModal, setShowReadOnlyEscModal] = useState(false);
  const [selectedEscId, setSelectedEscId] = useState<number | null>(null);

  const { data: picrPositionData } = useGetPicrPositionDetailsQuery({
    variables: {
      caseId,
    },
    skip: caseType !== ComplianceCaseTypeEnum.PaymentInstructionChangeReview,
  });

  const hasUnassignedProfiles = useMemo(() => {
    if (!picrPositionData?.picrPositionDetailsByCaseId || picrRevampEnabled) return false;
    return picrPositionData.picrPositionDetailsByCaseId.some(
      (position) => !position.linkedProfile && !position.successorCases?.length,
    );
  }, [picrPositionData, picrRevampEnabled]);

  const hasUnresolvedScreeningMatches = useMemo(() => {
    if (!picrPositionData?.picrPositionDetailsByCaseId || picrRevampEnabled) return false;
    return picrPositionData.picrPositionDetailsByCaseId.some((position) => {
      if (position.successorCases?.length) return false;
      if (!position.linkedProfile?.screeningRecord) return false;

      return (
        position.linkedProfile.screeningRecord.status === ScreeningRecordStatus.ReviewNeeded
      );
    });
  }, [picrPositionData, picrRevampEnabled]);

  const handleSubmitWithCheck = (submitAction: () => void, action?: ComplianceCaseAction) => {
    if (action === "return_to_maker") {
      submitAction();
      return;
    }

    if (caseType === ComplianceCaseTypeEnum.PaymentInstructionChangeReview) {
      if (hasUnresolvedScreeningMatches) {
        toast.error("Cannot close case: Unresolved screening matches found on linked profiles");
        return;
      }

      if (hasUnassignedProfiles) {
        setShowWarningModal(true);
        setPendingSubmitAction(() => submitAction);
        return;
      }
    }

    submitAction();
  };

  const handleConfirmSubmit = () => {
    if (pendingSubmitAction) {
      pendingSubmitAction();
      setShowWarningModal(false);
      setPendingSubmitAction(null);
    }
  };

  const getRightSideControl = (props: Props, actions: string[]) => {
    const canShowEscMenu = shouldRenderEscalationsMenu(
      props.caseType,
      props.escalations,
      internalEscalationsEnabled,
    );
    const escMenuFlags = getEscalationsMenuFlags(
      props.caseResolution,
      props.caseId,
      props.profilesForCase,
    );

    const relatedCasesPill = !props.caseIsClosed ? (
      <RelatedCasesPill
        currentCaseId={props.caseId}
        investingProfileId={props.profilesForCase?.investingProfile?.id}
        caseIsClosed={props.caseIsClosed}
      />
    ) : null;
    const escalationsMenu = canShowEscMenu ? (
      <EscalationsMenu
        caseId={props.caseId}
        escalations={props.escalations}
        profilesForCase={props.profilesForCase}
        linkMode={escMenuFlags.linkMode}
        linkUrl={escMenuFlags.linkUrl}
        successMode={escMenuFlags.successMode}
        onSelect={(id) => {
          if (internalEscalationsEnabled) {
            history.push(escalationTakeoverPaths.view(props.caseId, id), {
              from: history.location.pathname,
            });
            return;
          }
          setSelectedEscId(id);
          setShowReadOnlyEscModal(true);
        }}
      />
    ) : null;

    const readOnlyRightSide =
      relatedCasesPill || escalationsMenu ? (
        <Flex alignItems="center" gap={Sizes.SM} className={rightAlignedControlWrapper}>
          {relatedCasesPill}
          {escalationsMenu}
        </Flex>
      ) : null;

    if (props.caseIsClosed) return readOnlyRightSide;
    if (!props.assignee) return readOnlyRightSide;
    if (props.assignee && !props.assigneeRole) return readOnlyRightSide;

    if (props.assigneeRole === "maker") {
      return (
        <MakerControlsSelectMenu
          caseId={props.caseId}
          actions={actions}
          caseStatus={props.caseStatus}
          caseSubstatus={props.caseSubstatus}
          caseType={props.caseType}
          escalations={props.escalations}
          profilesForCase={props.profilesForCase}
          caseResolution={props.caseResolution}
          onSubmit={handleSubmitWithCheck}
        />
      );
    }

    if (props.assigneeRole === "checker") {
      return (
        <CheckerControlsSelectMenu
          caseId={props.caseId}
          actions={actions}
          caseStatus={props.caseStatus}
          caseSubstatus={props.caseSubstatus}
          caseType={props.caseType}
          onSubmit={handleSubmitWithCheck}
          escalationsCount={props.escalationsCount}
          escalations={props.escalations}
          profilesForCase={props.profilesForCase}
          caseResolution={props.caseResolution}
        />
      );
    }

    return null;
  };

  return (
    <>
      <Flex
        justifyContent="space-between"
        data-testid="case-details-footer"
        className={caseDetailsFooterWrapper}
      >
        {caseIsClosed ? (
          isCaseReopeningBlocked ? (
            // empty placeholder to keep right-side control right-aligned with space-between layout
            <div />
          ) : (
            <ClosedControls caseId={caseId} />
          )
        ) : assignee ? (
          <UnassignControls caseId={caseId} assignee={assignee} assigneeRole={assigneeRole} />
        ) : (
          <AssignControls caseId={caseId} />
        )}

        {getRightSideControl(
          {
            assignee,
            assigneeRole,
            caseId,
            caseIsClosed,
            caseStatus,
            caseSubstatus,
            caseType,
            programId,
            escalation,
            escalationsCount,
            escalations,
            profilesForCase,
            caseResolution,
          },
          actions,
        )}
      </Flex>
      <UnassignedProfileWarningModal
        show={showWarningModal}
        onHide={() => {
          setShowWarningModal(false);
          setPendingSubmitAction(null);
        }}
        onContinue={handleConfirmSubmit}
      />
      {showReadOnlyEscModal && selectedEscId != null && (
        <ProposedEscalationsModal
          show={showReadOnlyEscModal}
          onCloseModal={() => setShowReadOnlyEscModal(false)}
          caseType={caseType}
          caseStatus={caseStatus as NonNullable<typeof caseStatus>}
          caseId={caseId}
          escalation={escalations?.find((e) => e?.id === selectedEscId)}
          profilesForCase={profilesForCase}
        />
      )}
    </>
  );
};

export default CaseDetailsFooter;
